import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { Tag, Plus, Trash2, Pencil, Save, X, Check } from "lucide-react";
import { toast } from "sonner";

const PRESET_COLORS = ["#0D9488", "#22C55E", "#2563EB", "#A855F7", "#FF3B30", "#64748B", "#EC4899", "#06B6D4"];

const emptyForm = { name: "", description: "", color: "#0D9488", sort_order: 0 };

export default function CategoryManager({ open, onOpenChange, onChanged }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/community/categories");
      setCats(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) { load(); setEditingId(null); setForm(emptyForm); } }, [open, load]);

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({ name: c.name, description: c.description || "", color: c.color || "#0D9488", sort_order: c.sort_order || 0 });
  };

  const cancelEdit = () => { setEditingId(null); setForm(emptyForm); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        color: form.color,
        sort_order: Number(form.sort_order) || 0,
      };
      if (editingId) {
        await api.patch(`/community/categories/${editingId}`, payload);
        toast.success("Category updated");
      } else {
        await api.post("/community/categories", payload);
        toast.success("Category created");
      }
      setForm(emptyForm);
      setEditingId(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      const res = await api.delete(`/community/categories/${confirmDel.id}`);
      toast.success(`Deleted "${confirmDel.name}"`, {
        description: (res.data?.topics_unassigned || 0) > 0 ? `${res.data.topics_unassigned} topic(s) unassigned to General` : "No topics were using it.",
      });
      setConfirmDel(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-white border border-slate-200 text-slate-900 rounded-none max-w-2xl" data-testid="category-manager-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-3xl font-black flex items-center gap-2">
              <Tag className="w-6 h-6 text-[#0D9488]" /> Categories
            </DialogTitle>
            <DialogDescription className="font-mono-num text-[11px] uppercase tracking-[0.25em] text-slate-500">
              // organize discussion into topics
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-2 max-h-[70vh] overflow-y-auto pr-1">
            {/* Existing list */}
            <div className="space-y-2" data-testid="category-manager-list">
              <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">// {cats.length} categories</div>
              {loading ? (
                <div className="text-slate-500 text-xs font-mono-num uppercase tracking-widest">Loading…</div>
              ) : cats.length === 0 ? (
                <div className="text-slate-500 text-xs font-mono-num uppercase tracking-widest">No categories yet.</div>
              ) : (
                cats.map((c) => (
                  <div
                    key={c.id}
                    className={`border p-3 flex items-start gap-3 ${editingId === c.id ? "border-[#0D9488] bg-[#0D9488]/5" : "border-slate-200 bg-slate-50"}`}
                    data-testid={`category-row-${c.id}`}
                  >
                    <span className="w-3 h-3 mt-1 shrink-0" style={{ background: c.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-black uppercase tracking-tight text-slate-900 truncate">{c.name}</div>
                      {c.description && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{c.description}</div>}
                      <div className="font-mono-num text-[10px] text-slate-500 uppercase tracking-widest mt-1">
                        sort {c.sort_order} · slug {c.slug}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Button variant="ghost" size="icon" onClick={() => startEdit(c)}
                        className="h-7 w-7 rounded-none text-slate-500 hover:text-[#0D9488] hover:bg-slate-200"
                        data-testid={`category-edit-${c.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setConfirmDel(c)}
                        className="h-7 w-7 rounded-none text-slate-500 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"
                        data-testid={`category-delete-${c.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Form */}
            <form onSubmit={submit} className="space-y-3 border border-slate-200 bg-slate-50 p-4" data-testid="category-form">
              <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-1">
                {editingId ? "// edit category" : "// add new"}
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  maxLength={60} className="bg-slate-50 border-slate-200 rounded-none h-10" data-testid="category-form-name" required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Description (optional)</Label>
                <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  maxLength={500} className="bg-slate-50 border-slate-200 rounded-none resize-none" data-testid="category-form-description" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Color</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm({ ...form, color: c })}
                      className={`w-7 h-7 border-2 transition-transform ${form.color === c ? "scale-110 border-white" : "border-transparent hover:border-zinc-500"}`}
                      style={{ background: c }}
                      title={c}
                      data-testid={`category-form-color-${c}`}
                    />
                  ))}
                  <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="bg-slate-50 border-slate-200 rounded-none h-8 w-24 font-mono-num text-xs" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Sort order (lower = higher up)</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num" data-testid="category-form-sort" />
              </div>
              <div className="flex items-center gap-2 pt-1">
                {editingId && (
                  <Button type="button" variant="outline" onClick={cancelEdit}
                    className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800 h-9 uppercase tracking-widest text-xs font-semibold"
                    data-testid="category-form-cancel">
                    <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
                  </Button>
                )}
                <Button type="submit" disabled={saving}
                  className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none h-9 uppercase tracking-widest text-xs font-semibold flex-1 disabled:opacity-60"
                  data-testid="category-form-submit">
                  {editingId ? <><Check className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save changes"}</> : <><Plus className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Adding…" : "Add category"}</>}
                </Button>
              </div>
            </form>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
              className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800"
              data-testid="category-manager-close">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-white border border-slate-200 rounded-none text-slate-900" data-testid="category-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">Delete "{confirmDel?.name}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              Topics currently in this category will be unassigned (they stay on the board as uncategorized). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-slate-200 hover:bg-slate-50 rounded-none text-slate-800" data-testid="category-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[#FF3B30] hover:bg-[#c9291f] rounded-none uppercase tracking-widest font-semibold" data-testid="category-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
